/**
 * Phase 1.5.3 — 顧客 LIFF マイ予約一覧画面。
 *
 * Worker `GET /api/my-reservations` から取得した予約を、
 * 「未来 → 今日 → 過去」の順で表示する（Worker 側で日付昇順ソート済み）。
 * カードタップで詳細画面（renderMyReservationDetail）へ遷移。
 */

import { setRoot, showError } from "../render";
import { fetchMyReservations } from "../api";
import type { MyReservation, ReservationStatus } from "../types";

// 顧客視点のステータス表示。no_show は祐輔さんの管理用語「フォロー」と区別し、
// 顧客には completed と同じ「面談済」として見せる（顧客が自分を no_show 扱いと認識する違和感を回避）。
const STATUS_LABELS: Record<ReservationStatus, string> = {
  confirmed: "確定",
  completed: "面談済",
  no_show: "面談済",
  canceled: "キャンセル",
};

const CONTACT_LABELS: Record<string, string> = {
  zoom: "オンライン面談",
  line_call: "LINE通話",
};

/**
 * マイ予約一覧を表示する。
 */
export async function renderMyReservations(): Promise<void> {
  setRoot(loadingShell());

  let reservations: MyReservation[] = [];
  try {
    reservations = await fetchMyReservations();
  } catch (e: any) {
    showError(`予約の取得に失敗しました：${e?.message ?? e}`);
    setRoot(errorShell());
    bindBackButton();
    bindRetryButton();
    return;
  }

  paint(reservations);
}

function loadingShell(): string {
  return `
    ${headerHtml()}
    <div class="text-center text-gray-500 mt-8">読み込み中...</div>
  `;
}

function errorShell(): string {
  return `
    ${headerHtml()}
    <div class="text-center text-red-600 mt-8 px-2">
      予約の取得に失敗しました。<br>
      通信状況を確認して、もう一度お試しください。
    </div>
    <div class="mt-6 text-center">
      <button id="btn-retry" class="px-6 py-3 bg-brand text-white rounded-lg">
        再読み込み
      </button>
    </div>
  `;
}

function paint(reservations: MyReservation[]): void {
  setRoot(`
    ${headerHtml()}
    ${countHtml(reservations.length)}
    ${cardsHtml(reservations)}
  `);
  bindBackButton();
  bindCards();
}

function headerHtml(): string {
  return `
    <header class="mb-3 pb-2 border-b flex items-center justify-between">
      <button id="btn-back-to-calendar"
        class="text-sm text-gray-500 hover:text-gray-700 px-2 py-1">
        ‹ カレンダーへ
      </button>
      <h1 class="text-lg font-bold text-gray-900">📋 予約確認</h1>
      <span class="w-16"></span>
    </header>
  `;
}

function countHtml(n: number): string {
  return `<div class="text-sm text-gray-600 mb-2">${n} 件</div>`;
}

function cardsHtml(rs: MyReservation[]): string {
  if (rs.length === 0) {
    return `
      <div class="bg-white rounded-lg border border-gray-200 p-6 text-center text-gray-500 mt-2">
        予約はありません。<br>
        <button id="btn-go-calendar"
          class="mt-3 px-4 py-2 bg-brand text-white rounded text-sm">
          カレンダーから予約する
        </button>
      </div>
    `;
  }
  return `
    <div id="cards" class="flex flex-col gap-2">
      ${rs.map((r) => cardHtml(r)).join("")}
    </div>
  `;
}

function cardHtml(r: MyReservation): string {
  const dateLabel = formatDateWithWeekday(r.reservation_date);
  const isCanceled = r.status === "canceled";
  // 顧客視点では completed と no_show は同じ「面談済」として表示
  const isPast = r.status === "completed" || r.status === "no_show";
  // 確定 = 白（タップ可能な強調）/ 面談済 = 薄い青 / キャンセル = 薄いグレー
  const cardBg = isCanceled
    ? "bg-gray-50 border-gray-200 opacity-70"
    : isPast
    ? "bg-blue-50 border-blue-200"
    : "bg-white border-gray-200 hover:bg-gray-50";

  // confirmed かつキャンセル可能期間内なら「変更可」、期限超過の confirmed は「確定」のまま。
  // can_cancel は Worker 側で算出されるフラグ（reservation.can_cancel === true なら変更操作可能）。
  const isChangeable = r.status === "confirmed" && r.can_cancel;
  const badgeText = isChangeable ? "変更可" : STATUS_LABELS[r.status];
  const badgeColor = isChangeable
    ? "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300"
    : badgeClass(r.status);
  const statusBadge = `
    <span class="text-xs px-2 py-0.5 rounded ${badgeColor}">
      ${escapeHtml(badgeText)}
    </span>
  `;

  // キャンセル済みカードは「タップ → 詳細画面 → カレンダーから新しく予約する」のフローに繋がる。
  // 「タップで再予約可」と明示することで、グレーアウトでアクション不能と誤解されるのを防ぐ。
  const reReserveHint = isCanceled
    ? `<div class="text-xs text-emerald-700 mt-1.5 font-semibold">↪ タップで再予約可</div>`
    : "";

  return `
    <button
      data-id="${escapeHtml(r.id)}"
      class="text-left rounded-lg border p-3 w-full ${cardBg}">
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0 flex-1">
          <div class="font-semibold text-gray-900">
            ${escapeHtml(dateLabel)} ${escapeHtml(r.reservation_time)}
          </div>
          <div class="text-sm text-gray-700 mt-0.5">
            ${escapeHtml(CONTACT_LABELS[r.contact_method] ?? r.contact_method)}
          </div>
          ${reReserveHint}
        </div>
        ${statusBadge}
      </div>
    </button>
  `;
}

function badgeClass(status: ReservationStatus): string {
  switch (status) {
    case "confirmed":
      return "bg-green-100 text-green-800";
    case "completed":
    case "no_show":
      // 顧客視点では「面談済」として同じ表示にする
      return "bg-blue-100 text-blue-800";
    case "canceled":
      return "bg-gray-200 text-gray-600";
  }
}

function bindBackButton(): void {
  document.getElementById("btn-back-to-calendar")?.addEventListener("click", async () => {
    await goToCalendar();
  });
  document.getElementById("btn-go-calendar")?.addEventListener("click", async () => {
    await goToCalendar();
  });
}

function bindRetryButton(): void {
  document.getElementById("btn-retry")?.addEventListener("click", () => {
    void renderMyReservations();
  });
}

function bindCards(): void {
  const container = document.getElementById("cards");
  if (!container) return;
  container.addEventListener("click", async (ev) => {
    const target = (ev.target as HTMLElement).closest("[data-id]") as HTMLElement | null;
    if (!target) return;
    const id = target.getAttribute("data-id");
    if (!id) return;
    const { renderMyReservationDetail } = await import("./myReservationDetail");
    await renderMyReservationDetail(id);
  });
}

async function goToCalendar(): Promise<void> {
  const { renderStep1 } = await import("../steps/step1Date");
  await renderStep1();
}

// ===== ヘルパー =====

function formatDateWithWeekday(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  const w = ["日", "月", "火", "水", "木", "金", "土"][dt.getDay()];
  return `${m}/${d}(${w})`;
}

function escapeHtml(s: unknown): string {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
